/**
 * PrincipalAgentAffirmationPanel — Phase 16a1 reviewer panel.
 *
 * Runs the 5-indicator Principal/Agent classifier (ASC 606-10-55-36 to 55-40
 * + ASU 2016-08) on open to pre-populate suggestions, lets the reviewer edit
 * each indicator (value / evidence source / evidence text / rationale) and
 * affirm the role. AGENT affirmation requires a settlement model (PURE_AGENT
 * is the only usable value today — CONDUIT is gated on QRM Q9a-d and shown
 * visually disabled with a tooltip). PURE_AGENT additionally requires the
 * reviewer to attest that customer funds flow directly to the supplier
 * (606-10-55-36A). Submit calls POST /principal-agent-affirmation; known
 * server error codes surface as inline field errors.
 *
 * Props:
 *   pobId     — the POB to affirm
 *   pobLabel  — optional pretty label for the modal header
 *   onClose   — close without saving
 *   onSuccess — called after successful affirmation (caller should refetch)
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { API_V1, authFetch } from '../constants';
import { useGlobalEntity, withEntityParam } from '../hooks/useGlobalEntity';
import Modal from '../components/ui/Modal';

// ── Types (mirror backend SuggestionResult / AffirmationRequest) ────────

type EntityRole = 'PRINCIPAL' | 'AGENT' | 'UNDETERMINED';
type AgentSettlementModel = 'PURE_AGENT' | 'CONDUIT';

type IndicatorCode =
  | 'PRIMARY_RESPONSIBILITY'
  | 'INVENTORY_RISK'
  | 'PRICING_DISCRETION'
  | 'CUSTOMER_SELECTION'
  | 'CREDIT_RISK';

interface SuggestionIndicator {
  indicator_code: IndicatorCode;
  value: boolean;
  evidence_text: string;
  evidence_source: string;
  confidence: number | null;
}

interface SuggestionResult {
  pob_id: number;
  entity_role_suggested: EntityRole;
  indicators: SuggestionIndicator[];
  rationale: string;
  requires_reviewer_attestation: boolean;
}

interface PersistedIndicator {
  indicator_code: IndicatorCode;
  value: boolean;
  evidence_text: string | null;
  evidence_source: string | null;
  confidence: number | null;
  ai_suggested_value: boolean | null;
  override_of_ai_suggestion: boolean | null;
  reviewer_rationale: string | null;
}

interface AffirmationResponse {
  pob_id: number;
  entity_role: EntityRole;
  agent_settlement_model: AgentSettlementModel | null;
  indicators_persisted_count: number;
  bundled_pob_split_recommended?: boolean;
  warnings?: string[];
}

interface Props {
  pobId: number;
  pobLabel?: string;
  onClose: () => void;
  onSuccess?: (resp: AffirmationResponse) => void;
}

// ── Indicator metadata (ASC 606-10-55-38 + ASU 2016-08) ──────────────

const INDICATOR_META: Array<{
  code: IndicatorCode;
  label: string;
  citation: string;
  description: string;
}> = [
  {
    code: 'PRIMARY_RESPONSIBILITY',
    label: 'Primary responsibility for fulfillment',
    citation: '606-10-55-38(a)',
    description:
      'Entity is primarily responsible for the acceptability of the specified good or service.',
  },
  {
    code: 'INVENTORY_RISK',
    label: 'Inventory risk before transfer',
    citation: '606-10-55-38(b)',
    description:
      'Entity has inventory risk before the good is transferred to the customer (or after transfer via right of return).',
  },
  {
    code: 'PRICING_DISCRETION',
    label: 'Discretion in establishing prices',
    citation: '606-10-55-38(c)',
    description:
      'Entity has discretion in establishing the price for the specified good or service.',
  },
  {
    code: 'CUSTOMER_SELECTION',
    label: 'Discretion over customer selection',
    citation: 'ASU 2016-08 BC18 — control indicator',
    description:
      'Entity has discretion over which customers it will transact with (a qualitative control indicator).',
  },
  {
    code: 'CREDIT_RISK',
    label: 'Credit risk exposure',
    citation: 'ASU 2016-08 BC18 — control indicator',
    description:
      'Entity bears the credit risk for the amount receivable from the customer.',
  },
];

const EVIDENCE_SOURCES: string[] = [
  'CONTRACT_CLAUSE',
  'MSA_SECTION',
  'STATEMENT_OF_WORK',
  'PRICING_SCHEDULE',
  'INVOICING_TERMS',
  'INVENTORY_SYSTEM',
  'CREDIT_TERMS',
  'VENDOR_AGREEMENT',
  'OTHER',
];

// ── Known backend error codes (for inline field surfacing) ───────────

const KNOWN_ERROR_CODES = {
  PA_INDICATORS_INCOMPLETE: 'All 5 indicators must be provided.',
  AGENT_COMMISSION_NOT_SET: 'Agent commission rate must be configured on the POB before affirmation.',
  AGENT_RATIONALE_REQUIRED: 'Agent settlement rationale is required (min 30 characters).',
  CONDUIT_PATH_NOT_YET_AVAILABLE:
    'CONDUIT path is gated on QRM Q9a-d partner consultation. Use PURE_AGENT or wait for 16a1b.',
  PURE_AGENT_FUNDS_FLOW_NOT_ATTESTED:
    'PURE_AGENT requires reviewer attestation that customer funds flow directly to the supplier (ASC 606-10-55-36A).',
} as const;

type ErrorCode = keyof typeof KNOWN_ERROR_CODES;

// ── Component ─────────────────────────────────────────────────────

interface IndicatorRowState {
  code: IndicatorCode;
  value: boolean;
  aiValue: boolean | null;
  evidenceSource: string;
  evidenceText: string;
  reviewerRationale: string;
  confidence: number | null;
}

function emptyRow(code: IndicatorCode): IndicatorRowState {
  return {
    code,
    value: false,
    aiValue: null,
    evidenceSource: 'CONTRACT_CLAUSE',
    evidenceText: '',
    reviewerRationale: '',
    confidence: null,
  };
}

export default function PrincipalAgentAffirmationPanel({
  pobId,
  pobLabel,
  onClose,
  onSuccess,
}: Props) {
  const { entity } = useGlobalEntity();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [suggestion, setSuggestion] = useState<SuggestionResult | null>(null);

  const [role, setRole] = useState<'PRINCIPAL' | 'AGENT' | ''>('');
  const [settlementModel, setSettlementModel] = useState<AgentSettlementModel | ''>('');
  const [settlementRationale, setSettlementRationale] = useState('');
  const [fundsFlowAttested, setFundsFlowAttested] = useState(false);

  const [rows, setRows] = useState<IndicatorRowState[]>(() =>
    INDICATOR_META.map((m) => emptyRow(m.code)),
  );

  const [submitting, setSubmitting] = useState(false);
  const [bannerError, setBannerError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<ErrorCode, true>>>({});
  const [success, setSuccess] = useState<AffirmationResponse | null>(null);

  // Initial load: fetch persisted indicators first; if empty, run classifier
  // to pre-populate suggestions.
  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      // 1. Get persisted indicators (if any)
      const persistedRes = await authFetch(
        withEntityParam(
          `${API_V1}/revenue/pobs/${pobId}/principal-agent-indicators`,
          entity,
        ),
      );
      let persisted: PersistedIndicator[] = [];
      if (persistedRes.ok) {
        const data = await persistedRes.json();
        persisted = data.indicators || data || [];
      }

      // 2. Run classifier for suggestion (always — used for AI suggestion pills)
      const suggRes = await authFetch(
        withEntityParam(
          `${API_V1}/revenue/pobs/${pobId}/classify-principal-agent`,
          entity,
        ),
        { method: 'POST' },
      );
      let sugg: SuggestionResult | null = null;
      if (suggRes.ok) {
        sugg = await suggRes.json();
        setSuggestion(sugg);
      }

      // Merge: start from AI suggestion, overlay with persisted reviewer values
      const merged: IndicatorRowState[] = INDICATOR_META.map((m) => {
        const suggInd = sugg?.indicators.find((i) => i.indicator_code === m.code);
        const persInd = persisted.find((i) => i.indicator_code === m.code);
        const aiValue = suggInd?.value ?? null;
        if (persInd) {
          return {
            code: m.code,
            value: persInd.value,
            aiValue: persInd.ai_suggested_value ?? aiValue,
            evidenceSource: persInd.evidence_source || 'CONTRACT_CLAUSE',
            evidenceText: persInd.evidence_text || '',
            reviewerRationale: persInd.reviewer_rationale || '',
            confidence: persInd.confidence,
          };
        }
        return {
          code: m.code,
          value: suggInd?.value ?? false,
          aiValue,
          evidenceSource: suggInd?.evidence_source || 'CONTRACT_CLAUSE',
          evidenceText: suggInd?.evidence_text || '',
          reviewerRationale: '',
          confidence: suggInd?.confidence ?? null,
        };
      });
      setRows(merged);

      // Pre-select the suggested role (reviewer can change)
      if (sugg && sugg.entity_role_suggested !== 'UNDETERMINED') {
        setRole(sugg.entity_role_suggested);
      }
    } catch (e: any) {
      setLoadError(e?.message || 'Failed to load principal/agent data.');
    } finally {
      setLoading(false);
    }
  }, [pobId, entity]);

  useEffect(() => { load(); }, [load]);

  // ── Derived validation state ───────────────────────────────────

  const overrideCount = useMemo(
    () => rows.filter((r) => r.aiValue !== null && r.aiValue !== r.value).length,
    [rows],
  );

  const rationaleChars = settlementRationale.trim().length;
  const allIndicatorsHaveEvidence = rows.every(
    (r) => r.evidenceText.trim().length > 0,
  );

  const canSubmit = useMemo(() => {
    if (!role) return false;
    if (!allIndicatorsHaveEvidence) return false;
    if (role === 'AGENT') {
      if (!settlementModel) return false;
      if (settlementModel === 'CONDUIT') return false; // UI gate — backend will reject too
      if (rationaleChars < 30) return false;
      if (settlementModel === 'PURE_AGENT' && !fundsFlowAttested) return false;
    }
    return true;
  }, [role, settlementModel, rationaleChars, fundsFlowAttested, allIndicatorsHaveEvidence]);

  // ── Submit ────────────────────────────────────────────────────

  const updateRow = (code: IndicatorCode, patch: Partial<IndicatorRowState>) => {
    setRows((prev) => prev.map((r) => (r.code === code ? { ...r, ...patch } : r)));
  };

  const submit = async () => {
    if (!canSubmit || !role) return;
    setSubmitting(true);
    setBannerError(null);
    setFieldErrors({});
    try {
      const body: any = {
        entity_role: role,
        indicators_confirmed: rows.map((r) => ({
          indicator_code: r.code,
          value: r.value,
          evidence_text: r.evidenceText.trim(),
          evidence_source: r.evidenceSource,
          confidence: r.confidence,
          ai_suggested_value: r.aiValue,
          override_of_ai_suggestion:
            r.aiValue !== null && r.aiValue !== r.value,
          reviewer_rationale: r.reviewerRationale.trim() || null,
        })),
      };
      if (role === 'AGENT') {
        body.agent_settlement_model = settlementModel;
        body.agent_settlement_rationale = settlementRationale.trim();
        if (settlementModel === 'PURE_AGENT') {
          body.customer_funds_flow_direct_to_supplier = fundsFlowAttested;
        }
      }
      const res = await authFetch(
        withEntityParam(
          `${API_V1}/revenue/pobs/${pobId}/principal-agent-affirmation`,
          entity,
        ),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        },
      );
      if (!res.ok) {
        let detailText = `HTTP ${res.status}`;
        try {
          const data = await res.json();
          const detail = data?.detail || data?.error || data?.message || data;
          const detailStr = typeof detail === 'string' ? detail : JSON.stringify(detail);
          detailText = detailStr;
          const hit = (Object.keys(KNOWN_ERROR_CODES) as ErrorCode[]).find((k) =>
            detailStr.toUpperCase().includes(k),
          );
          if (hit) {
            setFieldErrors({ [hit]: true });
            setBannerError(KNOWN_ERROR_CODES[hit]);
          } else {
            setBannerError(detailStr);
          }
        } catch {
          setBannerError(detailText);
        }
        return;
      }
      const resp: AffirmationResponse = await res.json();
      setSuccess(resp);
      if (onSuccess) onSuccess(resp);
    } catch (e: any) {
      setBannerError(e?.message || 'Submit failed.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────

  const headerLine = pobLabel
    ? `${pobLabel} — Principal/Agent Affirmation`
    : `POB #${pobId} — Principal/Agent Affirmation`;

  return (
    <Modal
      open={true}
      onClose={onClose}
      title={headerLine}
      description="ASC 606-10-55-36 to 55-40 + ASU 2016-08. Reviewer affirms role; all 5 indicators are persisted."
      size="lg"
      footer={
        success ? (
          <button
            onClick={onClose}
            style={btnStyle('primary')}
          >Close</button>
        ) : (
          <>
            <button
              onClick={onClose}
              disabled={submitting}
              style={btnStyle('secondary')}
            >Cancel</button>
            <button
              onClick={submit}
              disabled={!canSubmit || submitting}
              style={{
                ...btnStyle(canSubmit ? 'primary' : 'disabled'),
                cursor: submitting ? 'wait' : (canSubmit ? 'pointer' : 'not-allowed'),
              }}
            >{submitting ? 'Submitting...' : 'Submit Affirmation'}</button>
          </>
        )
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {loading && (
          <div style={{ fontSize: 13, color: 'var(--text-hint)' }}>
            Loading indicators and running classifier...
          </div>
        )}

        {loadError && (
          <div style={errorBoxStyle}>{loadError}</div>
        )}

        {success && (
          <div style={successBoxStyle}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>Affirmation saved</div>
            <div style={{ fontSize: 12 }}>
              {success.indicators_persisted_count} indicator(s) persisted. Role:{' '}
              <strong>{success.entity_role}</strong>
              {success.agent_settlement_model && (
                <> · settlement model: <strong>{success.agent_settlement_model}</strong></>
              )}
              {success.bundled_pob_split_recommended && (
                <div style={{ marginTop: 6, padding: 8, background: 'rgba(234,179,8,0.1)', border: '1px solid var(--warning)', borderRadius: 4, color: 'var(--warning)' }}>
                  Bundled POB split recommended — see Assess Warranty for child split workflow.
                </div>
              )}
              {success.warnings && success.warnings.length > 0 && (
                <ul style={{ margin: '6px 0 0 16px', fontSize: 11 }}>
                  {success.warnings.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              )}
            </div>
          </div>
        )}

        {bannerError && !success && (
          <div style={errorBoxStyle}>{bannerError}</div>
        )}

        {!loading && !loadError && !success && (
          <>
            {/* Classifier rationale (read-only preview) */}
            {suggestion && suggestion.rationale && (
              <div style={infoBoxStyle}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-hint)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>
                  Classifier suggestion ({suggestion.entity_role_suggested})
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                  {suggestion.rationale}
                </div>
              </div>
            )}

            {/* Role selector */}
            <fieldset style={fieldsetStyle}>
              <legend style={legendStyle}>Reviewer role determination</legend>
              <div style={{ display: 'flex', gap: 12 }}>
                <label style={radioLabelStyle}>
                  <input
                    type="radio"
                    name="pa-role"
                    checked={role === 'PRINCIPAL'}
                    onChange={() => setRole('PRINCIPAL')}
                  />
                  <span>
                    <strong>PRINCIPAL</strong>
                    <span style={{ color: 'var(--text-hint)', marginLeft: 6, fontSize: 11 }}>
                      — recognize revenue GROSS
                    </span>
                  </span>
                </label>
                <label style={radioLabelStyle}>
                  <input
                    type="radio"
                    name="pa-role"
                    checked={role === 'AGENT'}
                    onChange={() => setRole('AGENT')}
                  />
                  <span>
                    <strong>AGENT</strong>
                    <span style={{ color: 'var(--text-hint)', marginLeft: 6, fontSize: 11 }}>
                      — recognize commission NET
                    </span>
                  </span>
                </label>
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-hint)', marginTop: 6 }}>
                UNDETERMINED is not permitted at affirmation — reviewer judgment required.
              </div>
            </fieldset>

            {/* Agent settlement sub-form */}
            {role === 'AGENT' && (
              <fieldset style={fieldsetStyle}>
                <legend style={legendStyle}>Agent settlement model</legend>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <label style={radioLabelStyle}>
                    <input
                      type="radio"
                      name="pa-settlement"
                      checked={settlementModel === 'PURE_AGENT'}
                      onChange={() => setSettlementModel('PURE_AGENT')}
                    />
                    <span>
                      <strong>PURE_AGENT</strong>
                      <span style={{ color: 'var(--text-hint)', marginLeft: 6, fontSize: 11 }}>
                        — customer funds flow directly to supplier; entity records commission only
                      </span>
                    </span>
                  </label>

                  <label
                    style={{
                      ...radioLabelStyle,
                      cursor: 'not-allowed',
                      opacity: 0.55,
                    }}
                    title="CONDUIT posting path gated on QRM Q9a-d partner consultation. Use PURE_AGENT or wait for 16a1b."
                  >
                    <input
                      type="radio"
                      name="pa-settlement"
                      disabled
                      checked={settlementModel === 'CONDUIT'}
                      onChange={() => { /* no-op — disabled */ }}
                    />
                    <span>
                      <span style={{ display: 'inline-block', marginRight: 6 }} aria-label="locked">[locked]</span>
                      <strong>CONDUIT</strong>
                      <span style={{ color: 'var(--text-hint)', marginLeft: 6, fontSize: 11 }}>
                        — gated on QRM Q9a-d (16a1b)
                      </span>
                    </span>
                  </label>
                </div>

                {settlementModel === 'PURE_AGENT' && (
                  <div style={{ marginTop: 10 }}>
                    <label style={{ display: 'flex', gap: 6, alignItems: 'flex-start', fontSize: 12 }}>
                      <input
                        type="checkbox"
                        checked={fundsFlowAttested}
                        onChange={(e) => setFundsFlowAttested(e.target.checked)}
                        style={{ marginTop: 3 }}
                      />
                      <span>
                        <strong>I attest that customer funds flow directly to the supplier</strong>
                        <span style={{ display: 'block', color: 'var(--text-hint)', fontSize: 11, marginTop: 2 }}>
                          ASC 606-10-55-36A — required condition for PURE_AGENT posting.
                        </span>
                      </span>
                    </label>
                    {fieldErrors.PURE_AGENT_FUNDS_FLOW_NOT_ATTESTED && (
                      <div style={inlineErrorStyle}>
                        {KNOWN_ERROR_CODES.PURE_AGENT_FUNDS_FLOW_NOT_ATTESTED}
                      </div>
                    )}
                  </div>
                )}

                <div style={{ marginTop: 10 }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
                    Settlement rationale (min 30 chars)
                  </label>
                  <textarea
                    value={settlementRationale}
                    onChange={(e) => setSettlementRationale(e.target.value)}
                    placeholder="Describe the economic substance: who invoices the customer, who collects, how the supplier is paid, and why the indicators support an AGENT conclusion..."
                    style={textareaStyle}
                    rows={4}
                  />
                  <div style={{
                    fontSize: 11,
                    color: rationaleChars < 30 ? 'var(--warning)' : 'var(--text-hint)',
                    marginTop: 2,
                  }}>
                    {rationaleChars} / 30 chars{rationaleChars < 30 ? ' — more detail required' : ' — OK'}
                  </div>
                  {fieldErrors.AGENT_RATIONALE_REQUIRED && (
                    <div style={inlineErrorStyle}>
                      {KNOWN_ERROR_CODES.AGENT_RATIONALE_REQUIRED}
                    </div>
                  )}
                  {fieldErrors.AGENT_COMMISSION_NOT_SET && (
                    <div style={inlineErrorStyle}>
                      {KNOWN_ERROR_CODES.AGENT_COMMISSION_NOT_SET}
                    </div>
                  )}
                  {fieldErrors.CONDUIT_PATH_NOT_YET_AVAILABLE && (
                    <div style={inlineErrorStyle}>
                      {KNOWN_ERROR_CODES.CONDUIT_PATH_NOT_YET_AVAILABLE}
                    </div>
                  )}
                </div>
              </fieldset>
            )}

            {/* Indicator table */}
            <fieldset style={fieldsetStyle}>
              <legend style={legendStyle}>
                Control indicators (ASC 606-10-55-38 + ASU 2016-08)
              </legend>
              <div style={{ fontSize: 11, color: 'var(--text-hint)', marginBottom: 8 }}>
                Each indicator is persisted. No single indicator is dispositive — reviewer weighs the mix.
                Evidence text is required on every row.
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: 'var(--bg-surface)' }}>
                      <th style={tableHeadStyle}>Indicator</th>
                      <th style={tableHeadStyle}>AI suggestion</th>
                      <th style={tableHeadStyle}>Reviewer value</th>
                      <th style={tableHeadStyle}>Evidence source</th>
                      <th style={tableHeadStyle}>Evidence text *</th>
                      <th style={tableHeadStyle}>Rationale (opt.)</th>
                      <th style={tableHeadStyle}>Override</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => {
                      const meta = INDICATOR_META.find((m) => m.code === r.code)!;
                      const isOverride = r.aiValue !== null && r.aiValue !== r.value;
                      const evidenceMissing = r.evidenceText.trim().length === 0;
                      return (
                        <tr key={r.code} style={{ borderBottom: '1px solid var(--border-subtle)', verticalAlign: 'top' }}>
                          <td style={tableCellStyle}>
                            <div style={{ fontWeight: 600 }}>{meta.label}</div>
                            <div style={{ fontSize: 10, color: 'var(--text-hint)' }}>{meta.citation}</div>
                            <div style={{ fontSize: 10, color: 'var(--text-hint)', marginTop: 2 }}>{meta.description}</div>
                          </td>
                          <td style={tableCellStyle}>
                            {r.aiValue === null ? (
                              <span style={{ color: 'var(--text-hint)', fontSize: 11 }}>-</span>
                            ) : (
                              <span
                                style={{
                                  display: 'inline-block',
                                  padding: '2px 8px',
                                  borderRadius: 10,
                                  fontSize: 10,
                                  fontWeight: 600,
                                  background: r.aiValue ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)',
                                  color: r.aiValue ? 'var(--positive)' : 'var(--negative)',
                                }}
                              >
                                {r.aiValue ? 'TRUE' : 'FALSE'}
                              </span>
                            )}
                            {r.confidence !== null && r.aiValue !== null && (
                              <div style={{ fontSize: 10, color: 'var(--text-hint)', marginTop: 2 }}>
                                conf {(r.confidence * 100).toFixed(0)}%
                              </div>
                            )}
                          </td>
                          <td style={tableCellStyle}>
                            <label style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
                              <input
                                type="checkbox"
                                checked={r.value}
                                onChange={(e) => updateRow(r.code, { value: e.target.checked })}
                              />
                              <span style={{ fontWeight: 600, color: r.value ? 'var(--positive)' : 'var(--negative)' }}>
                                {r.value ? 'TRUE' : 'FALSE'}
                              </span>
                            </label>
                          </td>
                          <td style={tableCellStyle}>
                            <select
                              value={r.evidenceSource}
                              onChange={(e) => updateRow(r.code, { evidenceSource: e.target.value })}
                              style={selectStyle}
                            >
                              {EVIDENCE_SOURCES.map((s) => (
                                <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
                              ))}
                            </select>
                          </td>
                          <td style={tableCellStyle}>
                            <textarea
                              value={r.evidenceText}
                              onChange={(e) => updateRow(r.code, { evidenceText: e.target.value })}
                              placeholder="e.g. MSA Section 4.2 assigns primary acceptance responsibility..."
                              style={{
                                ...textareaStyle,
                                minHeight: 48,
                                borderColor: evidenceMissing ? 'var(--warning)' : 'var(--border-subtle)',
                              }}
                            />
                          </td>
                          <td style={tableCellStyle}>
                            <textarea
                              value={r.reviewerRationale}
                              onChange={(e) => updateRow(r.code, { reviewerRationale: e.target.value })}
                              placeholder="Optional — why override or why retain"
                              style={{ ...textareaStyle, minHeight: 48 }}
                            />
                          </td>
                          <td style={{ ...tableCellStyle, textAlign: 'center' }}>
                            {isOverride ? (
                              <span style={overrideBadgeStyle}>OVERRIDE</span>
                            ) : (
                              <span style={{ fontSize: 10, color: 'var(--text-hint)' }}>match</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div style={{
                marginTop: 10,
                padding: 10,
                background: overrideCount > 0 ? 'rgba(234,179,8,0.08)' : 'var(--bg-surface)',
                border: `1px solid ${overrideCount > 0 ? 'var(--warning)' : 'var(--border-subtle)'}`,
                borderRadius: 4,
                fontSize: 12,
              }}>
                <strong>Override count: {overrideCount} of {rows.length}</strong>
                <span style={{ color: 'var(--text-hint)', marginLeft: 8, fontSize: 11 }}>
                  Overrides surface in the SOX 404 DIVERGENCE control monitor.
                </span>
              </div>
              {fieldErrors.PA_INDICATORS_INCOMPLETE && (
                <div style={inlineErrorStyle}>
                  {KNOWN_ERROR_CODES.PA_INDICATORS_INCOMPLETE}
                </div>
              )}
            </fieldset>

            {!allIndicatorsHaveEvidence && (
              <div style={{ fontSize: 11, color: 'var(--warning)' }}>
                All 5 indicators require evidence text before submit.
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}

// ── Styles (inline — consistent with surrounding RevenueReviews.tsx idiom) ──

const fieldsetStyle: React.CSSProperties = {
  padding: 12,
  border: '1px solid var(--border-subtle)',
  borderRadius: 6,
  background: 'var(--bg-card)',
  margin: 0,
};

const legendStyle: React.CSSProperties = {
  padding: '0 6px',
  fontSize: 12,
  fontWeight: 700,
  color: 'var(--navy-900)',
};

const radioLabelStyle: React.CSSProperties = {
  display: 'flex',
  gap: 6,
  alignItems: 'center',
  cursor: 'pointer',
  fontSize: 13,
};

const tableHeadStyle: React.CSSProperties = {
  padding: '8px 10px',
  fontSize: 10,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: 'var(--text-hint)',
  borderBottom: '1px solid var(--border-subtle)',
  textAlign: 'left',
  whiteSpace: 'nowrap',
};

const tableCellStyle: React.CSSProperties = {
  padding: '8px 10px',
  fontSize: 11,
  color: 'var(--text-primary)',
  verticalAlign: 'top',
};

const textareaStyle: React.CSSProperties = {
  width: '100%',
  padding: 6,
  fontSize: 11,
  background: 'var(--bg-surface)',
  border: '1px solid var(--border-subtle)',
  borderRadius: 4,
  color: 'var(--text-primary)',
  resize: 'vertical',
  minHeight: 36,
  fontFamily: 'inherit',
  boxSizing: 'border-box',
};

const selectStyle: React.CSSProperties = {
  width: '100%',
  padding: '4px 6px',
  fontSize: 11,
  background: 'var(--bg-surface)',
  border: '1px solid var(--border-subtle)',
  borderRadius: 4,
  color: 'var(--text-primary)',
};

const errorBoxStyle: React.CSSProperties = {
  padding: 10,
  background: 'rgba(239,68,68,0.1)',
  border: '1px solid var(--negative)',
  borderRadius: 4,
  color: 'var(--negative)',
  fontSize: 12,
};

const successBoxStyle: React.CSSProperties = {
  padding: 10,
  background: 'rgba(34,197,94,0.1)',
  border: '1px solid var(--positive)',
  borderRadius: 4,
  color: 'var(--positive)',
  fontSize: 13,
};

const infoBoxStyle: React.CSSProperties = {
  padding: 10,
  background: 'var(--bg-surface)',
  border: '1px solid var(--border-subtle)',
  borderRadius: 4,
};

const inlineErrorStyle: React.CSSProperties = {
  marginTop: 6,
  fontSize: 11,
  color: 'var(--negative)',
};

const overrideBadgeStyle: React.CSSProperties = {
  display: 'inline-block',
  padding: '2px 6px',
  borderRadius: 3,
  fontSize: 9,
  fontWeight: 700,
  background: 'var(--warning)',
  color: 'white',
};

function btnStyle(variant: 'primary' | 'secondary' | 'disabled'): React.CSSProperties {
  if (variant === 'primary') {
    return {
      padding: '8px 16px', fontSize: 13, fontWeight: 600,
      background: 'var(--positive)', color: 'white',
      border: 'none', borderRadius: 4, cursor: 'pointer',
    };
  }
  if (variant === 'disabled') {
    return {
      padding: '8px 16px', fontSize: 13, fontWeight: 600,
      background: 'var(--bg-hover)', color: 'var(--text-hint)',
      border: 'none', borderRadius: 4, cursor: 'not-allowed',
    };
  }
  return {
    padding: '8px 16px', fontSize: 13,
    background: 'transparent', color: 'var(--text-secondary)',
    border: '1px solid var(--border-subtle)', borderRadius: 4, cursor: 'pointer',
  };
}
